import React, { useState, useCallback, useEffect, useMemo } from "react";
import "./orderchatadminView.scss";
import OverlayLoading from "../../../common/OverlayLoader/Overlayloader";
import { TitleHeader } from "../../../components";
import {
  FaUserShield,
  FaComments,
  FaRegClock,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaArrowLeft,
} from "react-icons/fa";
import { RiUser3Fill, RiUserStarFill, RiTruckFill } from "react-icons/ri";

import orderchatadminViewUtils from "./orderchatadminViewUtils";
import { BsFileEarmarkMusic, BsImage } from "react-icons/bs";
import { ucfirst } from "../../../utils/customFn";
import { useNavigate } from "react-router-dom";

const OrderChatAdminView = () => {
  const navigate = useNavigate();
  const breadcrumbs = [
    { label: "Pages", link: "" },
    { label: "Add Page", link: "" },
  ];
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    currentIndex: 0,
    images: [],
  });

  const { formData, location, id } = orderchatadminViewUtils();

  // Precompute all image attachments from chat messages
  const allImages = useMemo(() => {
    const images = [];
    formData?.forEach((message) => {
      if (message.attachments && message.attachments.length > 0) {
        message.attachments.forEach((attachment) => {
          if (attachment.file_type !== "audio") {
            images.push({
              url: attachment.file_url,
              original_name: attachment.original_name,
            });
          }
        });
      }
    });
    return images;
  }, [formData]);

  const getSenderColor = (sender) => {
    switch (sender) {
      case "driver":
        return "#3B82F6";
      case "client":
        return "#10B981";
      case "receiver":
        return "#F59E0B";
      case "system":
        return "#6B7280";
      default:
        return "#4F46E5";
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const openLightbox = useCallback(
    (imageUrl) => {
      const index = allImages.findIndex((img) => img.url === imageUrl);
      if (index !== -1) {
        setLightbox({
          isOpen: true,
          currentIndex: index,
          images: allImages,
        });
      }
    },
    [allImages]
  );

  const closeLightbox = useCallback(() => {
    setLightbox((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const goToPrevious = useCallback(() => {
    setLightbox((prev) => ({
      ...prev,
      currentIndex:
        (prev.currentIndex - 1 + prev.images.length) % prev.images.length,
    }));
  }, []);

  const goToNext = useCallback(() => {
    setLightbox((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length,
    }));
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    if (!lightbox.isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "Escape":
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightbox.isOpen, goToPrevious, goToNext, closeLightbox]);

  // Handle touch swipe
  const handleTouchStart = useCallback((e) => {
    setLightbox((prev) => ({
      ...prev,
      touchStartX: e.touches[0].clientX,
    }));
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      if (!lightbox.touchStartX) return;

      const touchEndX = e.changedTouches[0].clientX;
      const diff = lightbox.touchStartX - touchEndX;

      if (diff > 50) {
        goToNext();
      } else if (diff < -50) {
        goToPrevious();
      }
    },
    [lightbox.touchStartX, goToPrevious, goToNext]
  );

  const renderAttachments = (attachments) => {
    return attachments.map((attachment, index) => (
      <div key={index} className="attachment">
        {attachment.file_type === "audio" ? (
          <div className="audio-attachment">
            <BsFileEarmarkMusic className="audio-icon" />
            <audio controls>
              <source src={attachment.file_url} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        ) : (
          <div
            className="image-attachment"
            onClick={() => openLightbox(attachment.file_url)}
          >
            <div className="image-preview">
              <img src={attachment.file_url} alt="Chat attachment" />
              <div className="image-overlay">
                <BsImage className="image-icon" />
                <span>View Image</span>
              </div>
            </div>
            <div className="image-meta">
              <span>{attachment.original_name}</span>
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <>
      <TitleHeader heading="Order Details" breadcrumbs={breadcrumbs} />

      <OverlayLoading isLoading={false}>
        <div className="admin-chat-container">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="header-left">
              <button
                className="back-button"
                onClick={() => navigate(-1)} // Go back to previous page
                aria-label="Go back"
              >
                <FaArrowLeft className="back-icon" />
              </button>
              <FaComments className="header-icon" />
              <div>
                <h2>Order #{id} Chat</h2>
                <div className="order-info">
                  <span>
                    <RiTruckFill /> {location}
                  </span>
                  {/* <span><FaRegClock /> 20 Aug 2023</span> */}
                </div>
              </div>
            </div>
            <div className="admin-badge">
              <FaUserShield /> Admin View Only
            </div>
          </div>

          {/* Chat Messages */}
          <div className="chat-messages">
            {formData?.map((msg) => (
              <div key={msg.id} className={`message-bubble ${msg.sender}`}>
                {msg.sender !== "system" && (
                  <div
                    className="message-header"
                    style={{ color: getSenderColor(msg.sender) }}
                  >
                    <span className="avatar">
                      {msg.sender == "client" ? (
                        <RiUser3Fill />
                      ) : (
                        <RiTruckFill />
                      )}
                    </span>
                    <span className="sender-name">
                      {msg.name} <span>({ucfirst(msg?.sender)})</span>{" "}
                    </span>
                    <span className="message-time">{msg.timestamp}</span>
                  </div>
                )}

                <div className="message-content">
                  {msg.message && (
                    <div className="text-message">{msg.message}</div>
                  )}

                  {msg.attachments?.length > 0 && (
                    <div className="attachments">
                      {renderAttachments(msg.attachments)}
                    </div>
                  )}

                  {msg.sender === "system" && (
                    <div className="system-message">
                      <span>{msg.message}</span>
                      <small>{msg.timestamp}</small>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Lightbox Modal */}
          {lightbox.isOpen && lightbox.images.length > 0 && (
            <div className="lightbox-modal">
              <button
                className="nav-btn prev-btn"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>

              <div
                className="lightbox-content"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  src={lightbox.images[lightbox.currentIndex].url}
                  alt="Full size attachment"
                />
                <div className="image-title">
                  {lightbox.images[lightbox.currentIndex].original_name}
                </div>
                <button
                  className="close-btn"
                  onClick={closeLightbox}
                  aria-label="Close lightbox"
                >
                  <FaTimes />
                </button>
              </div>

              <button
                className="nav-btn next-btn"
                onClick={goToNext}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>

              <div className="image-counter">
                {lightbox.currentIndex + 1} / {lightbox.images.length}
              </div>
            </div>
          )}

          {/* Disabled Input Area */}
          <div className="chat-input-disabled">
            <div className="admin-notice">
              <FaUserShield /> Administrators can view conversations but cannot
              participate
            </div>
          </div>
        </div>
      </OverlayLoading>
    </>
  );
};

export default OrderChatAdminView;
